import { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { METRIKA_GOALS, type AdScreenName } from '@anon-chat/shared';
import { Button, Screen, Skeleton } from '@/components/ui';
import { api } from '@/shared/api';
import { errorText, useTranslations } from '@/shared/i18n';
import { isTelegramMode } from '@/shared/app-mode';
import { initTelegram, isInsideTelegram } from '@/shared/telegram';
import { useSocketEvents } from '@/shared/use-socket-events';
import { configureAds } from '@/shared/adsgram';
import { initMetrika, reachGoal, trackScreen } from '@/shared/metrika';
import { applyPageSettings, loadPageSettings, type PageSettings } from '@/shared/page-settings';
import { trackErrors } from '@/shared/diagnostics';
import { trackViewportHeight } from '@/shared/viewport';
import { useAdsStore, trackAdDevice } from '@/store/ads.store';
import { useChatStore } from '@/store/chat.store';
import { useSessionStore } from '@/store/session.store';
import { OnboardingScreen } from '@/features/onboarding/OnboardingScreen';
import { SavedChatsScreen } from '@/features/saved/SavedChatsScreen';
import { SearchScreen } from '@/features/search/SearchScreen';
import { ChatScreen } from '@/features/chat/ChatScreen';
import { WelcomeScreen } from '@/features/welcome/WelcomeScreen';
import { LandingScreen } from '@/features/landing/LandingScreen';
import { useBotLogin } from '@/shared/use-bot-login';

/** Названия экранов в отчётах Метрики. */
const SCREEN_TITLES: Record<AdScreenName, string> = {
  landing: 'Главная',
  onboarding: 'Анкета',
  search: 'Поиск собеседника',
  chat: 'Диалог',
  saved: 'Мои чаты',
};

export function App(): React.ReactNode {
  const dictionary = useTranslations();
  const { status, user, errorCode, login, loginAsGuest } = useSessionStore();
  const screen = useChatStore((state) => state.screen);
  const restoreChat = useChatStore((state) => state.restoreChat);

  const [welcomeSeen, setWelcomeSeen] = useState(false);
  // Веб-версия всегда начинается с лендинга, даже у вернувшегося гостя: это
  // главная страница сайта, и попадать сразу в анкету, минуя её, неожиданно.
  const [landingPassed, setLandingPassed] = useState(false);
  // Вход через бота ведёт туда же, куда и гостевой: с лендинга внутрь.
  const botLogin = useBotLogin(() => setLandingPassed(true));

  // Высота под экранную клавиатуру: см. trackViewportHeight.
  useEffect(() => trackViewportHeight(), []);

  // Последние ошибки прикладываются к обращению о неполадке: без них починка
  // начинается с угадывания.
  useEffect(() => trackErrors(), []);

  // Ширина окна решает, какой рекламный блок показывать: у сетей свои форматы
  // под телефон и под компьютер.
  useEffect(() => trackAdDevice(), []);

  // Счётчик Метрики вставлен в разметку через панель: приложение находит его
  // номер и дальше само сообщает о целях и сменах экрана.
  useEffect(() => initMetrika(), []);

  // Заголовки из панели. Загружаются один раз и не блокируют интерфейс:
  // без них страница работает на значениях, заданных при сборке.
  const [page, setPage] = useState<PageSettings | null>(null);
  useEffect(() => {
    void loadPageSettings().then((settings) => {
      if (settings) {
        applyPageSettings(settings);
        configureAds(settings);
        useAdsStore.getState().setConfig(settings.ads);
        setPage(settings);
      }
    });
  }, []);

  // Лендинг — тоже экран для рекламы: боковые полосы можно включить только на
  // нём, и приложение должно знать, где сейчас пользователь.
  const onLanding = !isTelegramMode && status !== 'ready' && !landingPassed;
  useEffect(() => {
    const current = onLanding ? 'landing' : screen;
    useAdsStore.getState().setScreen(current);
    // Для Метрики смена экрана — просмотр страницы: без этого у приложения
    // один просмотр за визит и недостоверная глубина.
    trackScreen(current, SCREEN_TITLES[current]);
  }, [onLanding, screen]);

  useEffect(() => {
    initTelegram();
    // В Telegram вход происходит сразу: initData уже есть. В вебе — только по
    // нажатию на лендинге: иначе каждый визит с поисковика заводил бы пустого
    // пользователя.
    if (isTelegramMode) {
      void login();
    }
  }, [login]);

  useSocketEvents(status === 'ready');

  // Восстановление диалога при повторном открытии: Telegram выгружает
  // мини-приложение при сворачивании, а вкладку в браузере просто закрывают.
  useEffect(() => {
    if (status !== 'ready') {
      return;
    }
    void api
      .activeChat()
      .then((chat) => {
        if (chat) {
          restoreChat(chat, chat.messages);
        }
      })
      .catch(() => undefined);
  }, [status, restoreChat]);

  // ─── Веб-версия: лендинг до входа ──────────────────────────────────────────

  if (onLanding) {
    return (
      <LandingScreen
        heading={page?.h1 ?? null}
        botLogin={botLogin}
        busy={status === 'authenticating'}
        error={status === 'error' ? errorText(errorCode) : null}
        onStart={() => {
          reachGoal(METRIKA_GOALS.START_CLICK);
          void loginAsGuest().then((ok) => {
            if (ok) {
              setLandingPassed(true);
            }
          });
        }}
      />
    );
  }

  if (status === 'authenticating' || status === 'idle') {
    return (
      <Screen className="gap-3 px-5 pt-16">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="mt-6 h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </Screen>
    );
  }

  if (status === 'error') {
    return (
      <Screen className="items-center justify-center gap-4 px-8 text-center">
        <p className="text-[16px] text-text">{errorText(errorCode)}</p>
        {isTelegramMode && !isInsideTelegram() ? (
          <p className="text-[13px] text-text-muted">
            Приложение открывается внутри Telegram. Вне клиента вход возможен только при
            включённом TELEGRAM_ALLOW_INSECURE_DEV_AUTH.
          </p>
        ) : null}
        <Button
          className="mt-2"
          onClick={() => void (isTelegramMode ? login() : loginAsGuest())}
        >
          {dictionary.common.retry}
        </Button>
      </Screen>
    );
  }

  // В вебе роль вступления играет лендинг, и приветственный экран после него
  // был бы вторым подряд. В Telegram лендинга нет: там мини-приложение
  // открывается сразу с анкеты, и без вступления человек не поймёт, куда попал.
  const showWelcome =
    isTelegramMode && !welcomeSeen && !user?.onboardingCompleted && screen === 'onboarding';

  return (
    <AnimatePresence mode="wait">
      {showWelcome ? (
        <WelcomeScreen key="welcome" onStart={() => setWelcomeSeen(true)} />
      ) : screen === 'chat' ? (
        <ChatScreen key="chat" botLogin={botLogin} issuesEnabled={page?.issuesEnabled ?? true} />
      ) : screen === 'saved' ? (
        <SavedChatsScreen key="saved" />
      ) : screen === 'search' ? (
        <SearchScreen key="search" />
      ) : (
        <OnboardingScreen key="onboarding" />
      )}
    </AnimatePresence>
  );
}
